import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Save, Code, AlertTriangle, Lock, Eye, EyeOff, Globe, ExternalLink, FileText } from 'lucide-react';
import ImageUpload from '../components/ImageUpload';

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [robotsData, setRobotsData] = useState({ robots_txt: '', sitemap_url: '' });
  const [savingRobots, setSavingRobots] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
    fetchRobots();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRobots = async () => {
    try {
      const response = await api.get('/admin/robots');
      setRobotsData(response.data);
    } catch (error) {
      console.error('Failed to fetch robots.txt settings');
    }
  };

  const handleSaveRobots = async () => {
    setSavingRobots(true);
    try {
      await api.put('/admin/robots', { robots_txt: robotsData.robots_txt });
      toast({ title: 'Success', description: 'robots.txt updated successfully' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update robots.txt',
        variant: 'destructive',
      });
    } finally {
      setSavingRobots(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast({ title: 'Success', description: 'Settings updated successfully' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({
        title: 'Error',
        description: 'New passwords do not match',
        variant: 'destructive',
      });
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'New password must be at least 6 characters',
        variant: 'destructive',
      });
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      });
      toast({ title: 'Success', description: 'Password changed successfully' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to change password',
        variant: 'destructive',
      });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Site Settings</h1>
          <p className="text-gray-600 mt-2">Manage global site configuration</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-black hover:bg-gray-800"
        >
          <Save size={20} className="mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="max-w-4xl space-y-6">
        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">General</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Site Name
              </label>
              <Input
                value={settings?.site_name || ''}
                onChange={(e) =>
                  setSettings({ ...settings, site_name: e.target.value })
                }
                data-testid="site-name-input"
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Logo Settings</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Dark Logo</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Used on light backgrounds (header, light sections)
                </p>
                <ImageUpload
                  value={settings?.logo_url || ''}
                  onChange={(url) => setSettings({ ...settings, logo_url: url })}
                  label="Dark Logo"
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Light Logo</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Used on dark backgrounds (footer, dark sections)
                </p>
                <ImageUpload
                  value={settings?.logo_url_light || ''}
                  onChange={(url) => setSettings({ ...settings, logo_url_light: url })}
                  label="Light Logo"
                />
              </div>
            </div>
          </div>
          
          {/* Logo Size Slider */}
          <div className="mt-6 pt-6 border-t">
            <h3 className="text-lg font-medium text-gray-800 mb-2">Logo Size</h3>
            <p className="text-sm text-gray-500 mb-4">
              Adjust the logo height displayed in the navigation bar
            </p>
            <div className="flex items-center gap-6">
              <div className="flex-1">
                <input
                  type="range"
                  min="24"
                  max="80"
                  value={settings?.logo_size || 40}
                  onChange={(e) => setSettings({ ...settings, logo_size: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-black"
                  data-testid="logo-size-slider"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Small (24px)</span>
                  <span>Large (80px)</span>
                </div>
              </div>
              <div className="text-center">
                <span className="text-2xl font-bold text-gray-900">{settings?.logo_size || 40}</span>
                <span className="text-sm text-gray-500 ml-1">px</span>
              </div>
            </div>
            {/* Logo Preview */}
            {settings?.logo_url && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Preview:</p>
                <div className="flex items-center gap-4">
                  <div className="bg-white p-3 rounded border">
                    <img 
                      src={settings.logo_url} 
                      alt="Logo preview" 
                      style={{ height: `${settings?.logo_size || 40}px` }}
                      className="object-contain"
                    />
                  </div>
                  {settings?.logo_url_light && (
                    <div className="bg-gray-900 p-3 rounded">
                      <img 
                        src={settings.logo_url_light} 
                        alt="Light logo preview" 
                        style={{ height: `${settings?.logo_size || 40}px` }}
                        className="object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Favicon</h2>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              The small icon that appears in browser tabs. Recommended size: 32x32 or 512x512 pixels (PNG or ICO format).
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <ImageUpload
                  value={settings?.favicon_url || ''}
                  onChange={(url) => setSettings({ ...settings, favicon_url: url })}
                  label="Favicon"
                />
              </div>
              <div className="flex flex-col justify-center">
                {settings?.favicon_url && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <img 
                          src={settings.favicon_url} 
                          alt="Favicon preview" 
                          className="w-8 h-8 object-contain"
                        />
                      </div>
                      <span className="text-sm text-gray-600">Browser tab preview</span>
                    </div>
                    <a 
                      href={settings.favicon_url} 
                      download="favicon.png"
                      className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Favicon
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Accent Color</h2>
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Used for highlighted text like event dates in hero sections
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={settings?.accent_color || '#19c85d'}
                  onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                  className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  value={settings?.accent_color || '#19c85d'}
                  onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                  placeholder="#19c85d"
                  className="w-32"
                />
              </div>
              <div 
                className="px-4 py-2 rounded text-white font-semibold"
                style={{ backgroundColor: settings?.accent_color || '#19c85d' }}
              >
                Preview
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">CTA Buttons</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary CTA Label
                </label>
                <Input
                  value={settings?.primary_cta_label || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, primary_cta_label: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary CTA Link
                </label>
                <Input
                  value={settings?.primary_cta_link || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, primary_cta_link: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secondary CTA Label
                </label>
                <Input
                  value={settings?.secondary_cta_label || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, secondary_cta_label: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secondary CTA Link
                </label>
                <Input
                  value={settings?.secondary_cta_link || ''}
                  onChange={(e) =>
                    setSettings({ ...settings, secondary_cta_link: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">SEO Defaults</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Title Suffix
              </label>
              <Input
                value={settings?.default_meta_title_suffix || ''}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    default_meta_title_suffix: e.target.value,
                  })
                }
                placeholder=" | Pulse NYC"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Meta Description
              </label>
              <Textarea
                value={settings?.default_meta_description || ''}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    default_meta_description: e.target.value,
                  })
                }
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Social Share Image (og:image)
              </label>
              <p className="text-sm text-gray-500 mb-3">
                This image appears when your site is shared on Facebook, LinkedIn, Twitter, etc. 
                Used as fallback when pages don't have their own image. Recommended: 1200x630 pixels.
              </p>
              <ImageUpload
                value={settings?.default_og_image || ''}
                onChange={(url) => setSettings({ ...settings, default_og_image: url })}
                label="Default Social Share Image"
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Footer</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Copyright Text
            </label>
            <Input
              value={settings?.copyright_text || ''}
              onChange={(e) =>
                setSettings({ ...settings, copyright_text: e.target.value })
              }
            />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Code size={24} className="text-gray-700" />
            <h2 className="text-xl font-bold text-gray-900">Custom Code Injection</h2>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-2">
              <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Use with caution</p>
                <p>Code entered here will be injected into all pages. Only add trusted scripts like Google Analytics, tracking pixels, or chat widgets. Invalid code may break your site.</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Header Code <span className="text-gray-500 font-normal">(before &lt;/head&gt;)</span>
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Add Google Analytics, Meta Pixel, or other tracking scripts that need to load early.
              </p>
              <Textarea
                value={settings?.header_code || ''}
                onChange={(e) =>
                  setSettings({ ...settings, header_code: e.target.value })
                }
                rows={6}
                placeholder={`<!-- Example: Google Analytics -->
<script async src="https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'GA_MEASUREMENT_ID');
</script>`}
                className="font-mono text-sm"
                data-testid="header-code-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Footer Code <span className="text-gray-500 font-normal">(before &lt;/body&gt;)</span>
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Add chat widgets, ShareThis buttons, or scripts that should load after page content.
              </p>
              <Textarea
                value={settings?.footer_code || ''}
                onChange={(e) =>
                  setSettings({ ...settings, footer_code: e.target.value })
                }
                rows={6}
                placeholder={`<!-- Example: Intercom Chat Widget -->
<script>
  window.intercomSettings = {
    api_base: "https://api-iam.intercom.io",
    app_id: "YOUR_APP_ID"
  };
</script>
<script>(function(){var w=window;var ic=w.Intercom;...})();</script>`}
                className="font-mono text-sm"
                data-testid="footer-code-input"
              />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={24} className="text-gray-700" />
            <h2 className="text-xl font-bold text-gray-900">SEO & Sitemap</h2>
          </div>
          
          <div className="space-y-6">
            {/* Sitemap Link */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-green-800">Sitemap.xml</h3>
                  <p className="text-sm text-green-700 mt-1">
                    Auto-generated sitemap including all published pages and blog posts
                  </p>
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  data-testid="view-sitemap-btn"
                >
                  <FileText size={18} />
                  View Sitemap
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>

            {/* Robots.txt Editor */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    robots.txt
                  </label>
                  <p className="text-sm text-gray-500">
                    Control how search engines crawl your site
                  </p>
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/robots.txt`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  View robots.txt <ExternalLink size={12} />
                </a>
              </div>
              <Textarea
                value={robotsData.robots_txt}
                onChange={(e) => setRobotsData({ ...robotsData, robots_txt: e.target.value })}
                rows={10}
                className="font-mono text-sm"
                placeholder="User-agent: *\nAllow: /"
                data-testid="robots-txt-input"
              />
              <div className="flex justify-end mt-3">
                <Button
                  onClick={handleSaveRobots}
                  disabled={savingRobots}
                  className="bg-black hover:bg-gray-800"
                  data-testid="save-robots-btn"
                >
                  {savingRobots ? 'Saving...' : 'Save robots.txt'}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Domain Settings */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={24} className="text-gray-700" />
            <h2 className="text-xl font-bold text-gray-900">Domain Settings</h2>
          </div>
          <p className="text-gray-600 mb-6">
            Configure your canonical domain and www redirect settings for SEO.
          </p>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Canonical Domain
              </label>
              <Input
                type="text"
                value={settings?.canonical_domain || ''}
                onChange={(e) => setSettings({ ...settings, canonical_domain: e.target.value })}
                placeholder="pulse.nyc"
                className="max-w-md"
                data-testid="canonical-domain-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your primary domain without "www" (e.g., pulse.nyc, example.com)
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="redirect-www"
                checked={settings?.redirect_www_to_non_www ?? true}
                onChange={(e) => setSettings({ ...settings, redirect_www_to_non_www: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                data-testid="redirect-www-checkbox"
              />
              <label htmlFor="redirect-www" className="text-sm text-gray-700">
                Redirect www to non-www
              </label>
            </div>
            <p className="text-xs text-gray-500 -mt-4 ml-7">
              When enabled, visitors to www.{settings?.canonical_domain || 'yourdomain.com'} will be redirected to {settings?.canonical_domain || 'yourdomain.com'}
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> For this redirect to work in production, ensure both domains 
                (www and non-www) point to your server. The redirect happens at the application level.
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lock size={24} className="text-gray-700" />
            <h2 className="text-xl font-bold text-gray-900">Change Password</h2>
          </div>
          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password
              </label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={changingPassword}
              className="bg-black hover:bg-gray-800"
              data-testid="change-password-btn"
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
